if (!customElements.get('swatch-accordion')) {
  class SwatchAccordion extends HTMLElement {
    connectedCallback() {
      this.group = this.querySelector('[data-swatch-group]');
      this.toggle = this.querySelector('[data-swatch-accordion-toggle]');
      this.panel = this.querySelector('[data-swatch-accordion-panel]');
      this.panelGroup = this.querySelector('[data-swatch-accordion-panel-group]') || this.panel;
      this.countEl = this.querySelector('[data-swatch-accordion-count]');
      this.toggleLabelEl = this.querySelector('[data-swatch-accordion-toggle-label]');

      if (!this.group || !this.toggle || !this.panel) return;

      this.visibleDesktop = parseInt(this.dataset.visibleDesktop, 10) || 6;
      this.visibleTablet = parseInt(this.dataset.visibleTablet, 10) || 5;
      this.visibleMobile = parseInt(this.dataset.visibleMobile, 10) || 4;
      this.animationDuration = parseInt(this.dataset.animationDuration, 10) || 300;
      this.stayOpenAfterSelection = this.dataset.stayOpenAfterSelection === 'true';
      this.moreLabelTemplate = this.dataset.moreLabel || '+{count} More';
      this.expandLabelTemplate = this.dataset.moreLabelExpand || '';
      this.collapseLabel = this.dataset.moreLabelCollapse || '';

      this.style.setProperty('--swatch-accordion-duration', `${this.animationDuration}ms`);

      this.itemSelector = this.dataset.itemSelector || '.swatch-label';
      this.allSwatches = Array.from(this.group.querySelectorAll(this.itemSelector));
      this.isOpen = false;
      this.overflowCount = 0;

      this.toggle.addEventListener('click', () => this.onToggleClick());
      this.group.addEventListener('change', () => this.onSelectionChange());
      this.panelGroup.addEventListener('change', () => this.onSelectionChange());

      this.onDocumentClick = this.onDocumentClick.bind(this);
      document.addEventListener('click', this.onDocumentClick);

      this.lastLayoutWidth = null;
      this.layout();

      // Observed width, not the accordion's own outer box: `layout()` moves
      // swatches in/out and toggles the panel's hidden/max-height, all of
      // which change the accordion's own size. Observing `this` directly
      // would make every layout() run re-trigger the observer on its own
      // output, queuing a fresh rAF + reflow on every firing indefinitely -
      // that main-thread churn is what was making the toggle's :hover
      // transition feel delayed. The swatch group's own box width only
      // changes with real container/breakpoint resizes, so observing it
      // (and short-circuiting in the callback when the reported width
      // matches the last one layout() itself measured) breaks the feedback
      // loop while still re-running layout() whenever the available width
      // actually changes. Both sides compare the observer's own
      // contentRect.width so there's no cross-API box-model mismatch.
      this.resizeObserver =
        window.ResizeObserver &&
        new ResizeObserver((entries) => {
          const width = Math.round(entries[0].contentRect.width);
          if (width === this.lastLayoutWidth) return;
          this.lastLayoutWidth = width;
          window.requestAnimationFrame(() => this.layout());
        });
      if (this.resizeObserver) {
        this.resizeObserver.observe(this.group);
      } else {
        window.addEventListener('resize', () => this.layout());
      }
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      document.removeEventListener('click', this.onDocumentClick);
    }

    // Closes the accordion when the user clicks anywhere outside it,
    // matching standard dropdown/accordion behavior - the toggle button
    // itself is excluded so its own click handler (which already flips
    // isOpen) doesn't get double-processed here.
    onDocumentClick(event) {
      if (!this.isOpen) return;
      if (this.contains(event.target)) return;
      this.isOpen = false;
      this.syncPanelState();
    }

    getVisibleCount() {
      const width = window.innerWidth;
      if (width >= 990) return this.visibleDesktop;
      if (width >= 750) return this.visibleTablet;
      return this.visibleMobile;
    }

    getInputFor(label) {
      return document.getElementById(label.getAttribute('for'));
    }

    moveSwatchTo(container, label) {
      const input = this.getInputFor(label);
      if (input) container.appendChild(input);
      container.appendChild(label);
    }

    layout() {
      if (!this.allSwatches.length) return;

      const slotCount = Math.max(1, this.getVisibleCount());
      const totalOverflow = Math.max(0, this.allSwatches.length - slotCount);

      if (totalOverflow === 0) {
        this.overflowCount = 0;
        this.removeAttribute('data-overflowing');
        this.toggle.hidden = true;

        this.allSwatches.forEach((label) => {
          label.removeAttribute('data-swatch-overflow');
          this.moveSwatchTo(this.group, label);
        });
        this.group.appendChild(this.toggle);

        this.isOpen = false;
        this.panel.hidden = true;
        this.panel.removeAttribute('data-open');
        this.panel.style.maxHeight = '';
        return;
      }

      // The trigger occupies one of the visible slots, so only
      // (slotCount - 1) real swatches show in row 1.
      const visibleCount = Math.max(1, slotCount - 1);
      this.overflowCount = this.allSwatches.length - visibleCount;

      this.setAttribute('data-overflowing', 'true');
      this.toggle.hidden = false;

      const visibleLabels = this.allSwatches.slice(0, visibleCount);
      const overflowLabels = this.allSwatches.slice(visibleCount);

      visibleLabels.forEach((label) => {
        label.removeAttribute('data-swatch-overflow');
        this.moveSwatchTo(this.group, label);
      });
      this.group.appendChild(this.toggle);

      overflowLabels.forEach((label) => {
        label.setAttribute('data-swatch-overflow', 'true');
        this.moveSwatchTo(this.panelGroup, label);
      });

      if (this.countEl) this.countEl.textContent = this.moreLabelTemplate.replace('{count}', this.overflowCount);

      this.updateToggleLabel();
      this.syncPanelState({ skipTransition: true });
    }

    updateToggleLabel() {
      if (!this.toggleLabelEl) return;
      this.toggleLabelEl.textContent = this.isOpen
        ? this.collapseLabel
        : this.expandLabelTemplate.replace('{count}', this.overflowCount);
    }

    onToggleClick() {
      this.isOpen = !this.isOpen;
      this.syncPanelState();
    }

    syncPanelState(options) {
      const skipTransition = Boolean(options && options.skipTransition);
      this.toggle.setAttribute('aria-expanded', String(this.isOpen));
      this.updateToggleLabel();

      if (this.isOpen) {
        this.panel.removeAttribute('data-open');
        this.panel.hidden = false;
        const targetHeight = this.panel.scrollHeight;
        this.panel.style.maxHeight = skipTransition ? 'none' : `${targetHeight}px`;
        if (skipTransition) {
          this.panel.setAttribute('data-open', 'true');
          return;
        }
        window.setTimeout(() => {
          if (!this.isOpen) return;
          this.panel.style.maxHeight = 'none';
          this.panel.setAttribute('data-open', 'true');
        }, this.animationDuration);
      } else {
        this.panel.removeAttribute('data-open');
        if (skipTransition) {
          this.panel.hidden = true;
          this.panel.style.maxHeight = '';
          return;
        }
        const currentHeight = this.panel.scrollHeight;
        this.panel.style.maxHeight = `${currentHeight}px`;
        requestAnimationFrame(() => {
          this.panel.style.maxHeight = '0px';
        });
        window.setTimeout(() => {
          if (!this.isOpen) this.panel.hidden = true;
        }, this.animationDuration);
      }
    }

    onSelectionChange() {
      if (this.isOpen && !this.stayOpenAfterSelection) {
        this.isOpen = false;
        this.syncPanelState();
      }
    }
  }

  customElements.define('swatch-accordion', SwatchAccordion);
}
